import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtTotals, sumByCurrency, fmtDateTime, ORDER_STATUSES, statusMeta } from "@/lib/format";
import { Plus, MagnifyingGlass, DotsThree, Car, ClockCounterClockwise, CalendarBlank, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p, w) => `${BACKEND_URL}/api/files/${p.path}${w ? `?w=${w}` : ""}`;
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [histItems, setHistItems] = useState([]);
  const [histOrder, setHistOrder] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const delOrder = async (o) => {
    if (!window.confirm(`Видалити наряд ${o.number}? Цю дію не можна скасувати.`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Наряд видалено"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const openHistory = async (o) => {
    setHistOrder(o);
    setHistOpen(true);
    try {
      const r = await api.get(`/orders/${o.id}/history`);
      setHistItems(r.data);
    } catch (e) { setHistItems([]); }
  };

  const load = () => api.get("/orders", { params: { search } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const changeStatus = async (o, status) => {
    try {
      await api.patch(`/orders/${o.id}/status`, { status });
      toast.success(`Статус змінено: ${statusMeta(status).label}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const handleDrop = (status) => {
    const o = orders.find((x) => x.id === dragId);
    setOverCol(null);
    setDragId(null);
    if (o && o.status !== status) changeStatus(o, status);
  };

  return (
    <div>
      <PageHeader
        title="Замовлення-наряди"
        subtitle="Канбан-дошка нарядів"
        action={
          <Button onClick={() => navigate("/orders/new")} data-testid="new-order-btn">
            <Plus size={18} className="mr-1" /> Новий наряд
          </Button>
        }
      />

      <div className="flex items-center gap-4 mb-5 flex-wrap">
        <div className="relative max-w-sm w-full">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Пошук: номер, клієнт, телефон, VIN, держномер..." value={search} data-testid="orders-search"
            onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="show-hidden-label">
          <Checkbox checked={showHidden} onCheckedChange={(v) => setShowHidden(Boolean(v))} data-testid="show-hidden-checkbox" />
          Показати скриті (Видано)
        </label>
      </div>

      <div className="flex flex-col md:flex-row flex-wrap xl:flex-nowrap gap-4 items-stretch">
        {ORDER_STATUSES.filter((st) => showHidden || st.key !== "delivered").map((st) => {
          const items = orders.filter((o) => o.status === st.key);
          return (
            <div key={st.key}
              className={`flex-1 min-w-[240px] basis-full md:basis-[calc(50%-0.5rem)] xl:basis-0 bg-card border rounded-lg flex flex-col transition-colors ${overCol === st.key ? "border-primary ring-2 ring-primary/40" : "border-border"}`}
              data-testid={`kanban-col-${st.key}`}
              onDragOver={(e) => { e.preventDefault(); if (overCol !== st.key) setOverCol(st.key); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setOverCol(null); }}
              onDrop={() => handleDrop(st.key)}>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.color }} />
                <span className="font-head font-bold text-sm">{st.label}</span>
                <span className="ml-auto text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="p-3 space-y-3 min-h-[120px]">
                {items.map((o) => (
                  <div key={o.id}
                    draggable
                    onDragStart={(e) => { setDragId(o.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    className={`bg-secondary/60 border border-border rounded-md p-3 min-h-[200px] flex flex-col hover:border-primary/50 transition-all cursor-pointer ${dragId === o.id ? "opacity-40" : ""}`}
                    data-testid={`order-card-${o.number}`}>
                    <div className="mb-3 -mx-3 -mt-3 rounded-t-md overflow-hidden h-[152px] bg-background/40 flex items-center justify-center border-b border-border">
                      {o.vehicle_photo ? (
                        <img src={photoUrl(o.vehicle_photo, 500)} alt={o.vehicle_label} draggable={false} loading="lazy" decoding="async" className="w-full h-full object-cover" data-testid={`order-photo-${o.number}`} />
                      ) : (
                        <Car size={34} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex items-start justify-between">
                      <span className="font-head font-bold text-sm">{o.number}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger data-testid={`order-menu-${o.number}`} onClick={(e) => e.stopPropagation()}>
                          <DotsThree size={20} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => navigate(`/orders/${o.id}`)}>Відкрити</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/orders/${o.id}/edit`)}>Редагувати</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openHistory(o)} data-testid={`order-history-${o.number}`}>
                            Історія змін
                          </DropdownMenuItem>
                          {ORDER_STATUSES.filter((s) => s.key !== o.status).map((s) => (
                            <DropdownMenuItem key={s.key} onClick={() => changeStatus(o, s.key)}>
                              → {s.label}
                            </DropdownMenuItem>
                          ))}
                          {user?.role === "admin" && (
                            <DropdownMenuItem onClick={() => delOrder(o)} data-testid={`order-delete-${o.number}`} className="text-destructive focus:text-destructive">
                              <Trash size={16} className="mr-2" /> Видалити наряд
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="text-sm mt-1">{o.client_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{o.vehicle_label}</div>
                    {o.vehicle_vin && (
                      <div className="text-[11px] text-muted-foreground font-mono truncate" data-testid={`order-vin-${o.number}`}>
                        VIN: {o.vehicle_vin}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1">
                      <CalendarBlank size={13} />
                      {o.created_at ? fmtDateTime(o.created_at) : "—"}
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-2">
                      <span className="text-xs text-muted-foreground">{o.master_name || "Без майстра"}</span>
                      <span className="font-semibold text-sm">{fmtTotals(o.totals_by_currency || sumByCurrency([...(o.services || []), ...(o.parts || [])]))}</span>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Порожньо</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={20} /> Історія змін · {histOrder?.number}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="history-list">
            {histItems.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Історія порожня</div>}
            <div className="relative pl-5">
              {histItems.map((h, i) => (
                <div key={h.id || i} className="relative pb-4 border-l border-border last:border-l-transparent" data-testid={`history-item-${i}`}>
                  <span className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full bg-primary" />
                  <div className="pl-4">
                    <div className="text-sm font-semibold">{h.action}</div>
                    {h.details && <div className="text-xs text-muted-foreground mt-0.5">{h.details}</div>}
                    <div className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(h.created_at)} · {h.user}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
